import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LuArrowLeft,
  LuInfo,
  LuMapPin,
  LuFileText,
  LuCheck,
  LuChevronRight,
  LuChevronLeft,
  LuLoader,
  LuSave,
} from "react-icons/lu";
import { FaExclamationCircle } from "react-icons/fa";
import api from "../../libs/axios";
import { SelectWithSearch, SelectOption } from "../../utils/select-with-search";
import { INC_PRIORITY_OPTIONS } from "./inc_sites";

const WORK_TYPE_OPTIONS = [
  { value: "INC", label: "INC" },
  { value: "OME", label: "OME" },
];

const TEAM_TYPE_OPTIONS = [
  { value: "inhouse", label: "Inhouse" },
  { value: "vendor", label: "Vendor" },
];

// Every INC project picker is limited to the projects the logged-in user is
// actually assigned to (user_projects), admins included — see the `assigned_only`
// branch in ProjectManagerController.getMyProjects.
export const ASSIGNED_PROJECTS_PARAMS = { assigned_only: true } as const;

const STEPS = [
  { number: 1, title: "Site Info", description: "Identification, project & work", icon: LuInfo },
  { number: 2, title: "Location", description: "Address and coordinates", icon: LuMapPin },
  { number: 3, title: "Contact & PO", description: "Contact, PO and allocation", icon: LuFileText },
];

const inputBase =
  "w-full px-3 py-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm";
const selectClass =
  "w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";
const cardClass = "bg-gray-50 border border-gray-200 rounded p-4 sm:p-6";
const cardTitleClass = "text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2";

// Module-level input so identity is stable across renders (prevents focus loss).
interface FieldProps {
  label: string;
  value: any;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  error?: string;
  placeholder?: string;
  textarea?: boolean;
  disabled?: boolean;
}
const Field: React.FC<FieldProps> = ({ label, value, onChange, type = "text", required, error, placeholder, textarea, disabled }) => (
  <div>
    <label className={labelClass}>
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {textarea ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        disabled={disabled}
        className={`${inputBase} resize-none ${error ? "border-red-300 bg-red-50" : "border-gray-300"}`}
      />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        step={type === "number" ? "any" : undefined}
        className={`${inputBase} ${error ? "border-red-300 bg-red-50" : "border-gray-300"}`}
      />
    )}
    {error && (
      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
        <FaExclamationCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

interface IncSiteFormProps {
  mode: "create" | "edit";
  siteId?: string | number;
}

const IncSiteForm: React.FC<IncSiteFormProps> = ({ mode, siteId }) => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<any>({
    site_code: "",
    reference_number: "",
    client_project_name: "",
    site_name: "",
    site_address: "",
    project_id: 0,
    activity_id: 0,
    work_type: "INC",
    priority: "medium",
    team_type: "inhouse",
    vendor_id: 0,
    state_circle_id: 0,
    district_id: 0,
    pincode: "",
    latitude: "",
    longitude: "",
    client_allocation_date: "",
    work_requirement: "",
    customer_requirement: "",
    contact_person: "",
    contact_number: "",
    po_number: "",
    po_date: "",
    is_po_shared: false,
    remarks: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stepValidation, setStepValidation] = useState<Record<number, boolean>>({ 1: false, 2: true, 3: true });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");

  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<SelectOption | null>(null);
  const [selectedState, setSelectedState] = useState<SelectOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<SelectOption | null>(null);

  const update = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
  };

  useEffect(() => {
    if (mode !== "edit" || !siteId) return;
    (async () => {
      try {
        const res = await api.get(`/inc/sites/${siteId}`);
        const s = res.data?.data;
        if (s) {
          setFormData((prev: any) => ({
            ...prev,
            ...s,
            client_allocation_date: s.client_allocation_date ? String(s.client_allocation_date).slice(0, 10) : "",
            po_date: s.po_date ? String(s.po_date).slice(0, 10) : "",
          }));
          if (s.project) setSelectedProject({ id: s.project.id, label: s.project.project_name, value: s.project.id });
          if (s.activity) setSelectedActivity({ id: s.activity.id, label: s.activity.activity_name, value: s.activity.id });
          if (s.state_circle) setSelectedState({ id: s.state_circle.id, label: s.state_circle.circle_name, value: s.state_circle.id });
          if (s.district) setSelectedDistrict({ id: s.district.id, label: s.district.name, value: s.district.id });
          if (s.vendor) setSelectedVendor({ id: s.vendor.id, label: s.vendor.vendor_full_name, value: s.vendor.id });
        }
      } catch {
        toast.error("Failed to load site");
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, siteId]);

  // Pure step validation — returns errors for a given step.
  const getStepErrors = (step: number, data = formData): Record<string, string> => {
    const e: Record<string, string> = {};
    if (step === 1) {
      if (!data.site_code?.toString().trim()) e.site_code = "Site code is required";
      else if (data.site_code.toString().trim().length < 3) e.site_code = "Minimum 3 characters";
      if (!data.project_id) e.project_id = "Project is required";
      if (!data.site_name?.toString().trim()) e.site_name = "Site name is required";
    }
    if (step === 2) {
      if (data.latitude !== "" && data.latitude != null && (Number(data.latitude) < -90 || Number(data.latitude) > 90))
        e.latitude = "Latitude must be between -90 and 90";
      if (data.longitude !== "" && data.longitude != null && (Number(data.longitude) < -180 || Number(data.longitude) > 180))
        e.longitude = "Longitude must be between -180 and 180";
      if (data.pincode && !/^\d{4,6}$/.test(String(data.pincode))) e.pincode = "Invalid pincode";
    }
    return e;
  };

  // Keep step-validity flags live so navigation reflects current data.
  useEffect(() => {
    setStepValidation({
      1: Object.keys(getStepErrors(1)).length === 0,
      2: Object.keys(getStepErrors(2)).length === 0,
      3: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const validateAndShow = (step: number): boolean => {
    const e = getStepErrors(step);
    setErrors((prev) => ({ ...prev, ...e }));
    return Object.keys(e).length === 0;
  };

  const isStepAccessible = (step: number): boolean => {
    if (step === 1) return true;
    if (step === 2) return stepValidation[1];
    return stepValidation[1] && stepValidation[2];
  };

  const goToStep = (step: number) => {
    if (step < currentStep || isStepAccessible(step)) {
      if (step > currentStep && !validateAndShow(currentStep)) return;
      setCurrentStep(step);
    }
  };

  const handleNext = () => {
    if (!validateAndShow(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
  };

  const handlePrevious = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    const allValid = [1, 2, 3].every((s) => validateAndShow(s));
    if (!allValid) {
      // jump to first invalid step
      const firstBad = [1, 2, 3].find((s) => Object.keys(getStepErrors(s)).length > 0);
      if (firstBad) setCurrentStep(firstBad);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        pincode: formData.pincode ? Number(formData.pincode) : undefined,
        latitude: formData.latitude ? Number(formData.latitude) : undefined,
        longitude: formData.longitude ? Number(formData.longitude) : undefined,
        activity_id: formData.activity_id || undefined,
        state_circle_id: formData.state_circle_id || undefined,
        district_id: formData.district_id || undefined,
        vendor_id: formData.team_type === "vendor" ? formData.vendor_id || undefined : undefined,
      };
      if (mode === "create") {
        await api.post("/inc/sites", payload);
      } else {
        await api.put(`/inc/sites/${siteId}`, payload);
      }
      toast.success(mode === "create" ? "Site created" : "Site updated");
      navigate("/inc/sites");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save site");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading...</div>;

  return (
    <div className="mx-auto">
      <button onClick={() => navigate("/inc/sites")} className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium mb-4">
        <LuArrowLeft className="w-4 h-4" /> Back to Sites
      </button>

      <div className="bg-white rounded border border-gray-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">
              {mode === "create" ? "Create INC Site" : "Edit INC Site"}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
              {mode === "create" ? "Add a new installation & commissioning site" : "Update site details"}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between w-full bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4 overflow-x-auto">
          <div className="flex items-center gap-6 min-w-max mx-auto">
            {STEPS.map((step, index) => {
              const isActive = currentStep === step.number;
              const isCompleted = stepValidation[step.number] && currentStep > step.number;
              const accessible = isStepAccessible(step.number);
              const Icon = step.icon;
              return (
                <React.Fragment key={step.number}>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => goToStep(step.number)}
                      disabled={!accessible}
                      className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 flex-shrink-0 ${
                        isActive
                          ? "border-blue-800 bg-blue-800 text-white"
                          : isCompleted
                            ? "border-green-600 bg-green-600 text-white"
                            : accessible
                              ? "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
                              : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {isCompleted && !isActive ? <LuCheck className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                    </button>
                    <div className="ml-3 hidden md:block">
                      <p className={`text-sm font-medium whitespace-nowrap ${isActive ? "text-blue-800" : isCompleted ? "text-green-600" : accessible ? "text-gray-900" : "text-gray-400"}`}>
                        {step.title}
                      </p>
                      <p className={`text-xs whitespace-nowrap ${isActive ? "text-blue-500" : isCompleted ? "text-green-500" : "text-gray-500"}`}>
                        {step.description}
                      </p>
                    </div>
                    <div className="ml-2 md:hidden">
                      <p className={`text-xs font-medium ${isActive ? "text-blue-800" : isCompleted ? "text-green-600" : "text-gray-500"}`}>{step.title}</p>
                    </div>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className={`w-10 sm:w-20 h-0.5 mx-3 ${stepValidation[step.number] && currentStep > step.number ? "bg-green-300" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-6">
            {currentStep === 1 && (
              <>
                <div className={cardClass}>
                  <h3 className={cardTitleClass}><LuInfo className="w-5 h-5 text-blue-800" /> Basic Site Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Site Code" value={formData.site_code} onChange={(v) => update("site_code", v)} required error={errors.site_code} placeholder="Unique site code" />
                    <Field label="Reference Number" value={formData.reference_number} onChange={(v) => update("reference_number", v)} />
                    <Field label="Site Name" value={formData.site_name} onChange={(v) => update("site_name", v)} required error={errors.site_name} />
                    <Field label="Client Project Name" value={formData.client_project_name} onChange={(v) => update("client_project_name", v)} />
                  </div>
                </div>

                <div className={cardClass}>
                  <h3 className={cardTitleClass}><LuFileText className="w-5 h-5 text-blue-800" /> Project & Work</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Project <span className="text-red-500">*</span></label>
                      <SelectWithSearch
                        value={selectedProject}
                        onChange={(o) => { setSelectedProject(o); update("project_id", o?.id || 0); }}
                        apiEndpoint="/projects/list"
                        dataKey="projects"
                        labelKey="project_name"
                        placeholder="Search projects..."
                        minSearchLength={0}
                        extraParams={ASSIGNED_PROJECTS_PARAMS}
                      />
                      {errors.project_id && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><FaExclamationCircle className="w-3 h-3" />{errors.project_id}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Activity</label>
                      <SelectWithSearch
                        value={selectedActivity}
                        onChange={(o) => { setSelectedActivity(o); update("activity_id", o?.id || 0); }}
                        apiEndpoint="/activities/list"
                        dataKey="activities"
                        labelKey="activity_name"
                        placeholder="Search activities..."
                        minSearchLength={0}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Work Type</label>
                      <select value={formData.work_type} onChange={(e) => update("work_type", e.target.value)} className={selectClass}>
                        {WORK_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Priority</label>
                      <select value={formData.priority} onChange={(e) => update("priority", e.target.value)} className={selectClass}>
                        {INC_PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Team Type</label>
                      <select value={formData.team_type} onChange={(e) => update("team_type", e.target.value)} className={selectClass}>
                        {TEAM_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    {formData.team_type === "vendor" && (
                      <div>
                        <label className={labelClass}>Vendor</label>
                        <SelectWithSearch
                          value={selectedVendor}
                          onChange={(o) => { setSelectedVendor(o); update("vendor_id", o?.id || 0); }}
                          apiEndpoint="/vendors/list"
                          dataKey="vendors"
                          labelKey="vendor_full_name"
                          placeholder="Search vendors..."
                          minSearchLength={0}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <div className={cardClass}>
                <h3 className={cardTitleClass}><LuMapPin className="w-5 h-5 text-blue-800" /> Site Location</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Field label="Site Address" value={formData.site_address} onChange={(v) => update("site_address", v)} textarea />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <SelectWithSearch
                      value={selectedState}
                      onChange={(o) => {
                        setSelectedState(o);
                        update("state_circle_id", o?.id || 0);
                        setSelectedDistrict(null);
                        update("district_id", 0);
                      }}
                      apiEndpoint="/state-circle/list"
                      dataKey="states"
                      labelKey="state_name"
                      placeholder="Search states..."
                      minSearchLength={0}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>District</label>
                    <SelectWithSearch
                      value={selectedDistrict}
                      onChange={(o) => { setSelectedDistrict(o); update("district_id", o?.id || 0); }}
                      apiEndpoint={selectedState ? `/state-circle/${selectedState.id}/districts` : ""}
                      dataKey="districts"
                      labelKey="name"
                      placeholder={selectedState ? "Search districts..." : "Select state first..."}
                      minSearchLength={0}
                      disabled={!selectedState}
                    />
                  </div>
                  <Field label="Pincode" value={formData.pincode} onChange={(v) => update("pincode", v)} error={errors.pincode} />
                  <div />
                  <Field label="Latitude" value={formData.latitude} onChange={(v) => update("latitude", v)} type="number" error={errors.latitude} />
                  <Field label="Longitude" value={formData.longitude} onChange={(v) => update("longitude", v)} type="number" error={errors.longitude} />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <>
                <div className={cardClass}>
                  <h3 className={cardTitleClass}><LuFileText className="w-5 h-5 text-blue-800" /> Contact & Purchase Order</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Contact Person" value={formData.contact_person} onChange={(v) => update("contact_person", v)} />
                    <Field label="Contact Number" value={formData.contact_number} onChange={(v) => update("contact_number", v)} type="tel" />
                    <Field label="PO Number" value={formData.po_number} onChange={(v) => update("po_number", v)} />
                    <Field label="PO Date" value={formData.po_date} onChange={(v) => update("po_date", v)} type="date" />
                    <div className="md:col-span-2 flex items-center gap-2">
                      <input
                        id="is_po_shared"
                        type="checkbox"
                        checked={!!formData.is_po_shared}
                        onChange={(e) => update("is_po_shared", e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="is_po_shared" className="text-sm text-gray-700">PO number shared at allocation</label>
                    </div>
                  </div>
                </div>

                <div className={cardClass}>
                  <h3 className={cardTitleClass}><LuInfo className="w-5 h-5 text-blue-800" /> Requirements & Allocation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Client Allocation Date" value={formData.client_allocation_date} onChange={(v) => update("client_allocation_date", v)} type="date" />
                    <div />
                    <div className="md:col-span-2">
                      <Field label="Work Requirement" value={formData.work_requirement} onChange={(v) => update("work_requirement", v)} textarea />
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Customer Requirement" value={formData.customer_requirement} onChange={(v) => update("customer_requirement", v)} textarea />
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Remarks" value={formData.remarks} onChange={(v) => update("remarks", v)} textarea />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="order-2 sm:order-1 flex items-center gap-3">
              <span className="text-sm text-gray-600">Step {currentStep} of {STEPS.length}</span>
              <div className="w-24 bg-gray-200 rounded-full h-1.5">
                <div className="bg-blue-800 h-1.5 rounded-full transition-all duration-300" style={{ width: `${(currentStep / STEPS.length) * 100}%` }} />
              </div>
            </div>
            <div className="order-1 sm:order-2 flex items-center gap-3 w-full sm:w-auto">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 transition-colors flex-1 sm:flex-none justify-center"
                >
                  <LuChevronLeft className="w-4 h-4" /> Previous
                </button>
              )}
              {currentStep < STEPS.length ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={submitting || !stepValidation[currentStep]}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  Next <LuChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !stepValidation[1] || !stepValidation[2]}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  {submitting ? (
                    <><LuLoader className="w-4 h-4 animate-spin" /> {mode === "create" ? "Creating..." : "Saving..."}</>
                  ) : (
                    <><LuSave className="w-4 h-4" /> {mode === "create" ? "Create Site" : "Save Changes"}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncSiteForm;
